<script setup lang="ts">
import type { CmsIconName } from '@pkg/cms-icons';
import type { Button } from '@pkg/sanity-utils/types/button';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import { ButtonGroup } from '../ButtonGroup';
import CmsIcon from '../Tiles/CmsIcon.vue';
import type { ContentItem } from './types';
import {
  getItemKey,
  getItemSubtitle,
  getItemTitle,
  isItemSubtitle,
  isItemTitle,
} from './types';

interface Props {
  icon?: CmsIconName;
  content?: ContentItem[] | boolean;
  button?: Button;
}

const props = defineProps<Props>();
</script>

<template>
  <div class="c-callout">
    <CmsIcon v-if="props.icon" :name="props.icon" class="c-callout__icon" />

    <template v-if="props.content">
      <h2 class="c-callout__title-group">
        <template v-for="item in props.content" :key="getItemKey(item)">
          <PortableEditor v-if="isItemTitle(item)" :content="getItemTitle(item)" class="c-callout__title" />
          <PortableEditor v-else-if="isItemSubtitle(item)" :content="getItemSubtitle(item)" class="c-callout__subtitle" />
        </template>
      </h2>
    </template>

    <div v-if="props.button" class="c-callout__action">
      <ButtonGroup :buttons="[props.button]" />
    </div>
  </div>
</template>

<style scoped>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

.c-callout {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.c-callout__icon {
  font-size: theme('fontSize.56');
  color: var(--callout-icon-color);
  position: relative;
  z-index: theme('zIndex.foreground');

  @screen tablet-wide {
    font-size: theme('spacing.96');
  }
}

.c-callout__title {
  @mixin h2-highlight;

  + .c-callout__subtitle {
    padding-top: 0.5rem;
  }
}

.c-callout__subtitle {
  @mixin h3;
  color: var(--theme-heading-color);
  text-transform: uppercase;
}

.c-callout__action {
  margin-top: theme('spacing.16');

  @screen tablet-wide {
    margin-top: theme('spacing.24');
  }
}

.c-callout__title-group {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: theme('zIndex.foreground');

  > .c-callout__title:first-child {
    margin-top: calc(theme('spacing.9') * -1);

    @screen tablet-wide {
      margin-top: calc(theme('spacing.20') * -1);
    }
  }
}
</style>
